import { useCallback, useEffect, useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, type Expense } from '../api/client'
import { ConfirmDialog, Toast, ClosedBanner, UndoToast } from '../components/ConfirmDialog'

function fmt(n: number | null | undefined) { return (n ?? 0).toLocaleString('ja-JP') }
// マイナス金額（返金・調整等）は「-¥5,000」のように符号を¥の前に出す
function fmtYen(n: number | null | undefined) {
  const v = n ?? 0
  return v < 0 ? `-¥${fmt(Math.abs(v))}` : `¥${fmt(v)}`
}
const MONTH_STORAGE_KEY = 'mangaos_month'

// JST基準の今月 YYYY-MM
function jstThisMonth(): string {
  return new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 7)
}

function getSavedMonth(): string {
  try {
    const saved = localStorage.getItem(MONTH_STORAGE_KEY)
    if (saved && /^\d{4}-\d{2}$/.test(saved)) return saved
  } catch {}
  return jstThisMonth()
}

function daysUntil(s: string | null) {
  if (!s) return null
  // JSTの今日0時と比較（"YYYY-MM-DD"はT00:00:00として扱うため+9hオフセットで比較）
  const todayJstMs = new Date(Date.now() + 9 * 60 * 60 * 1000)
  todayJstMs.setUTCHours(0, 0, 0, 0)
  const dueMs = new Date(s + 'T00:00:00+09:00').getTime()
  return Math.ceil((dueMs - todayJstMs.getTime()) / 86400000)
}

type SortState = { key: string; dir: 'asc' | 'desc' }

function SortTh({ label, colKey, sort, onSort, style }: {
  label: string; colKey: string; sort: SortState
  onSort: (key: string) => void; style?: CSSProperties
}) {
  const active = sort.key === colKey
  return (
    <th onClick={() => onSort(colKey)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}>
      {label}
      <span style={{ marginLeft: 3, opacity: active ? 1 : 0.2, fontSize: 10 }}>
        {active && sort.dir === 'desc' ? '▼' : '▲'}
      </span>
    </th>
  )
}

function DueChip({ days, isPaid }: { days: number | null; isPaid: number }) {
  if (isPaid) return <span className="badge badge-green">支払済</span>
  if (days === null) return <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>期日なし</span>
  if (days < 0)   return <span className="deadline-chip deadline-critical">{Math.abs(days)}日超過</span>
  if (days === 0) return <span className="deadline-chip deadline-critical">今日</span>
  if (days <= 3)  return <span className="deadline-chip deadline-critical">残{days}日</span>
  if (days <= 10) return <span className="deadline-chip deadline-warning">残{days}日</span>
  return <span className="deadline-chip deadline-ok">残{days}日</span>
}

// ★ 追加: 支払元口座バッジ
function AccountBadge({ accountKey }: { accountKey: string }) {
  if (accountKey === 'personal_manga') {
    return (
      <span className="badge badge-blue" style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
        個人口座
      </span>
    )
  }
  return (
    <span className="badge badge-gray" style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
      事業用
    </span>
  )
}

// ★ 追加: 支払区分バッジ
function CashflowBadge({ scope }: { scope: string }) {
  if (scope === 'tax') {
    return <span className="badge badge-red" style={{ fontSize: 10 }}>税金</span>
  }
  if (scope === 'personal_non_business') {
    return <span className="badge badge-amber" style={{ fontSize: 10 }}>個人費</span>
  }
  return null // 'business' は省略
}

// ── 口座・区分の選択肢 ──────────────────────────────────────────
const ACCOUNT_OPTIONS = [
  { value: 'business',       label: '事業用口座' },
  { value: 'personal_manga', label: '漫画家用個人口座' },
] as const

const CASHFLOW_OPTIONS = [
  { value: 'business',              label: '事業費' },
  { value: 'tax',                   label: '税金' },
  { value: 'personal_non_business', label: '事業費にできない支払い' },
] as const

// ────────────────────────────────────────────────────────────────
// 支出追加モーダル
// ────────────────────────────────────────────────────────────────
function AddExpenseModal({ onClose, onSaved }: {
  onClose: () => void
  onSaved: (id: string, desc: string) => void
}) {
  const [form, setForm] = useState({
    category: 'other_variable', description: '', amount: '',
    payment_method: 'bank_transfer', due_at: '',
    related_month: jstThisMonth(), notes: '',
    payment_account_key: 'business' as string,
    cashflow_scope: 'business' as string,
  })
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const set = (k: string, v: string) => {
    setForm((f) => ({ ...f, [k]: v }))
    setErrors((e) => { const next = { ...e }; delete next[k]; return next })
  }

  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.description.trim()) e.description = '内容を入力してください'
    if (!form.amount || Number.isNaN(Number(form.amount)) || Number(form.amount) === 0) {
      e.amount = '金額を入力してください（0以外の数値。返金・調整はマイナスで入力できます）'
    }
    return e
  }

  const handleSubmit = async () => {
    const e = validate()
    if (Object.keys(e).length > 0) { setErrors(e); return }
    setSaving(true)
    try {
      const result = await api.expenses.create({
        category: form.category, description: form.description,
        amount: Number(form.amount), payment_method: form.payment_method,
        due_at: form.due_at || undefined, related_month: form.related_month || undefined,
        notes: form.notes || undefined,
        payment_account_key: form.payment_account_key,
        cashflow_scope: form.cashflow_scope,
      })
      onSaved((result as any)?.id ?? '', form.description)
    } finally { setSaving(false) }
  }

  return (
    // ★ 入力途中の内容が消えないよう、背景クリックでは閉じない。
    //   閉じられるのは ✕ ボタン・キャンセルボタン・保存成功時のみ。
    <div className="overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">支出を追加</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="grid-2">
            <div className="form-group">
              <label>カテゴリ</label>
              <select value={form.category} onChange={(e) => set('category', e.target.value)}>
                <option value="pension">国民年金</option>
                <option value="health_insurance">国民健康保険</option>
                <option value="rent">家賃</option>
                <option value="credit_card">クレジットカード</option>
                <option value="dc_pension">確定拠出年金</option>
                <option value="assistant_salary">アシスタント給与</option>
                <option value="transport">交通費</option>
                <option value="meal">食費</option>
                <option value="school">修復学校</option>
                <option value="other_fixed">その他固定費</option>
                <option value="other_variable">その他変動費</option>
              </select>
            </div>
            <div className="form-group">
              <label>支払方法</label>
              <select value={form.payment_method} onChange={(e) => set('payment_method', e.target.value)}>
                <option value="auto_debit">口座引落</option>
                <option value="credit_card">クレジットカード</option>
                <option value="bank_transfer">振込</option>
                <option value="cash">現金</option>
              </select>
            </div>
          </div>
          {/* ★ 追加: 支払元口座・支払区分 */}
          <div className="grid-2">
            <div className="form-group">
              <label>支払元口座</label>
              <select value={form.payment_account_key} onChange={(e) => set('payment_account_key', e.target.value)}>
                {ACCOUNT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>支払区分</label>
              <select value={form.cashflow_scope} onChange={(e) => set('cashflow_scope', e.target.value)}>
                {CASHFLOW_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>内容 <span style={{ color: 'var(--red)' }}>*</span></label>
            <input type="text" placeholder="内容を入力" value={form.description}
              onChange={(e) => set('description', e.target.value)}
              style={{ borderColor: errors.description ? 'var(--red)' : undefined }} />
            {errors.description && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.description}</div>}
          </div>
          <div className="grid-3">
            <div className="form-group">
              <label>金額（円）<span style={{ color: 'var(--red)' }}>*</span></label>
              <input type="number" placeholder="0" value={form.amount}
                onChange={(e) => set('amount', e.target.value)}
                style={{ borderColor: errors.amount ? 'var(--red)' : undefined }} />
              {errors.amount && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 3 }}>{errors.amount}</div>}
            </div>
            <div className="form-group">
              <label>支払期日</label>
              <input type="date" value={form.due_at} onChange={(e) => set('due_at', e.target.value)} />
            </div>
            <div className="form-group">
              <label>対象月</label>
              <input type="month" value={form.related_month} onChange={(e) => set('related_month', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? '保存中...' : '追加'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// 支出編集モーダル
// ────────────────────────────────────────────────────────────────
function EditExpenseModal({ expense, onClose, onSaved }: {
  expense: Expense
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState({
    category:            expense.category            ?? 'other_variable',
    description:         expense.description         ?? '',
    amount:              String(expense.amount        ?? ''),
    payment_method:      expense.payment_method      ?? 'bank_transfer',
    due_at:              expense.due_at              ?? '',
    notes:               expense.notes               ?? '',
    payment_account_key: expense.payment_account_key ?? 'business',
    cashflow_scope:      expense.cashflow_scope      ?? 'business',
  })
  const [saving,  setSaving]  = useState(false)
  const [saveErr, setSaveErr] = useState<string | null>(null)
  const set = (k: string, v: string) => { setForm((f) => ({ ...f, [k]: v })); setSaveErr(null) }

  const handleSave = async () => {
    if (!form.description.trim()) { setSaveErr('内容を入力してください'); return }
    const amount = Number(form.amount)
    if (form.amount.trim() === '' || Number.isNaN(amount)) { setSaveErr('金額を数値で入力してください'); return }
    setSaving(true); setSaveErr(null)
    try {
      await api.expenses.update(expense.id, {
        category:            form.category,
        description:         form.description,
        amount,
        payment_method:      form.payment_method,
        due_at:              form.due_at || null,
        notes:               form.notes  || null,
        payment_account_key: form.payment_account_key,
        cashflow_scope:      form.cashflow_scope,
      })
      onSaved()
    } catch (e: any) {
      const msg: string = e.message ?? '保存に失敗しました'
      setSaveErr(msg.includes('締め済み') || msg.includes('423')
        ? 'この月は締め済みのため変更できません。'
        : msg)
    } finally { setSaving(false) }
  }

  return (
    // ★ 入力途中の内容が消えないよう、背景クリックでは閉じない。
    //   閉じられるのは ✕ ボタン・キャンセルボタン・保存成功時のみ。
    <div className="overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">支出を編集</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="grid-2">
            <div className="form-group">
              <label>カテゴリ</label>
              <select value={form.category} onChange={(e) => set('category', e.target.value)}>
                <option value="pension">国民年金</option>
                <option value="health_insurance">国民健康保険</option>
                <option value="rent">家賃</option>
                <option value="credit_card">クレジットカード</option>
                <option value="dc_pension">確定拠出年金</option>
                <option value="assistant_salary">アシスタント給与</option>
                <option value="transport">交通費</option>
                <option value="meal">食費</option>
                <option value="school">修復学校</option>
                <option value="other_fixed">その他固定費</option>
                <option value="other_variable">その他変動費</option>
              </select>
            </div>
            <div className="form-group">
              <label>支払方法</label>
              <select value={form.payment_method} onChange={(e) => set('payment_method', e.target.value)}>
                <option value="auto_debit">口座引落</option>
                <option value="credit_card">クレジットカード</option>
                <option value="bank_transfer">振込</option>
                <option value="cash">現金</option>
              </select>
            </div>
          </div>
          {/* ★ 追加: 支払元口座・支払区分 */}
          <div className="grid-2">
            <div className="form-group">
              <label>支払元口座</label>
              <select value={form.payment_account_key} onChange={(e) => set('payment_account_key', e.target.value)}>
                {ACCOUNT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>支払区分</label>
              <select value={form.cashflow_scope} onChange={(e) => set('cashflow_scope', e.target.value)}>
                {CASHFLOW_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>内容</label>
            <input type="text" value={form.description}
              onChange={(e) => set('description', e.target.value)} placeholder="Adobe CC" />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>金額（円）</label>
              <input type="number" value={form.amount}
                onChange={(e) => set('amount', e.target.value)} placeholder="例：12000" />
            </div>
            <div className="form-group">
              <label>期日</label>
              <input type="date" value={form.due_at} onChange={(e) => set('due_at', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>メモ（任意）</label>
            <input type="text" value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="補足など" />
          </div>
          {saveErr && (
            <div style={{
              color: 'var(--red)', fontSize: 12, marginTop: 8,
              padding: '8px 14px', background: 'var(--red-light)', borderRadius: 6,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span>⚠</span>
              <span style={{ flex: 1 }}>{saveErr}</span>
              <button onClick={() => setSaveErr(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 14 }}>
                ✕
              </button>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// メインページ
// ────────────────────────────────────────────────────────────────
export default function Expenses() {
  const navigate = useNavigate()
  const [month, setMonthState] = useState<string>(getSavedMonth)
  const setMonth = (m: string) => {
    setMonthState(m)
    try { localStorage.setItem(MONTH_STORAGE_KEY, m) } catch {}
  }

  const [expenses, setExpenses] = useState<Expense[]>([])
  const [summary, setSummary]   = useState<any>(null)
  const [closeData, setClose]   = useState<any>(null)
  const [loading, setLoading]   = useState(true)
  const [showAdd, setShowAdd]   = useState(false)
  const [unpaidSort, setUnpaidSort] = useState<SortState>({ key: 'due_at', dir: 'asc' })
  const [paidSort,   setPaidSort]   = useState<SortState>({ key: 'paid_at', dir: 'desc' })
  const [toast,        setToast]        = useState<{ msg: string; ok: boolean } | null>(null)
  const [undoState,    setUndoState]    = useState<{ id: string; desc: string } | null>(null)
  const [confirm,      setConfirm]      = useState<{ id: string; desc: string } | null>(null)
  const [unmarkConfirm, setUnmarkConfirm] = useState<{ id: string; desc: string } | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; desc: string; isPaid: boolean } | null>(null)
  const [editExpense,  setEditExpense]  = useState<Expense | null>(null)

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok })
    setTimeout(() => setToast(null), 4000)
  }

  const load = async () => {
    setLoading(true)
    const [exps, sum, cl] = await Promise.all([
      api.expenses.list({ month }),
      api.expenses.summary(month),
      api.monthlyCloses.get(month).catch(() => ({ status: 'open' })),
    ])
    setExpenses(exps)
    setSummary(sum)
    setClose(cl)
    setLoading(false)
  }

  useEffect(() => { load() }, [month])

  const isClosed = (closeData?.status ?? 'open') === 'closed'

  const doMarkPaid = async (id: string) => {
    try {
      await api.expenses.markPaid(id)
      await load()
      showToast('支払済にしました')
    } catch (e: any) {
      if (e.message?.includes('締め済み') || e.message?.includes('MONTH_CLOSED')) {
        showToast('この月は締め済みのため変更できません。月次レポートから締めを解除してください。', false)
      } else {
        showToast(e.message, false)
      }
    }
  }

  const doMarkUnpaid = async (id: string) => {
    try {
      await api.expenses.markUnpaid(id)
      await load()
      showToast('支払済を解除しました')
    } catch (e: any) {
      if (e.message?.includes('締め済み') || e.message?.includes('MONTH_CLOSED')) {
        showToast('この月は締め済みのため変更できません。月次レポートから締めを解除してください。', false)
      } else {
        showToast(e.message ?? '解除に失敗しました', false)
      }
    }
  }

  const doDeleteExpense = async (id: string) => {
    try {
      await api.expenses.delete(id)
      await load()
      showToast('支出を削除しました')
    } catch (e: any) {
      showToast(e.message ?? '削除に失敗しました', false)
    }
  }

  const unpaid = expenses.filter((e) => !e.is_paid)
  const paid   = expenses.filter((e) =>  e.is_paid)

  const toggleUnpaidSort = useCallback((key: string) => {
    setUnpaidSort((prev) => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' })
  }, [])
  const togglePaidSort = useCallback((key: string) => {
    setPaidSort((prev) => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' })
  }, [])

  const sortedUnpaid = useMemo(() => {
    const list = [...unpaid]
    const { key, dir } = unpaidSort
    list.sort((a: any, b: any) => {
      let av = a[key] ?? ''
      let bv = b[key] ?? ''
      if (key === 'amount') return dir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av)
      if (typeof av === 'string' && typeof bv === 'string')
        return dir === 'asc' ? av.localeCompare(bv, 'ja') : bv.localeCompare(av, 'ja')
      return dir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av)
    })
    return list
  }, [unpaid, unpaidSort])

  const sortedPaid = useMemo(() => {
    const list = [...paid]
    const { key, dir } = paidSort
    list.sort((a: any, b: any) => {
      let av = a[key] ?? ''
      let bv = b[key] ?? ''
      if (key === 'amount') return dir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av)
      if (typeof av === 'string' && typeof bv === 'string')
        return dir === 'asc' ? av.localeCompare(bv, 'ja') : bv.localeCompare(av, 'ja')
      return dir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av)
    })
    return list
  }, [paid, paidSort])

  return (
    <div>
      {undoState && (
        <UndoToast
          msg={`「${undoState.desc}」を追加しました`}
          onUndo={async () => { await api.expenses.delete(undoState.id); await load() }}
          onClose={() => setUndoState(null)}
        />
      )}
      {toast && <Toast msg={toast.msg} ok={toast.ok} onClose={() => setToast(null)} />}

      {confirm && (
        <ConfirmDialog
          title="支払済にする"
          message={`「${confirm.desc}」を支払済にします。\nよろしいですか？`}
          confirmLabel="支払済にする"
          onConfirm={async () => { await doMarkPaid(confirm.id); setConfirm(null) }}
          onClose={() => setConfirm(null)}
        />
      )}

      {unmarkConfirm && (
        <ConfirmDialog
          title="支払済を解除する"
          message={`「${unmarkConfirm.desc}」の支払済を解除します。\n口座残高に返金されます。よろしいですか？`}
          confirmLabel="解除する"
          onConfirm={async () => { await doMarkUnpaid(unmarkConfirm.id); setUnmarkConfirm(null) }}
          onClose={() => setUnmarkConfirm(null)}
        />
      )}

      {deleteConfirm && (
        <ConfirmDialog
          title="支出を削除しますか？"
          message={[
            `「${deleteConfirm.desc}」を削除します。`,
            deleteConfirm.isPaid ? '支払済の記録も削除されます。' : '',
            'この操作は取り消せません。',
          ].filter(Boolean).join('\n')}
          confirmLabel="削除する"
          onConfirm={async () => { await doDeleteExpense(deleteConfirm.id); setDeleteConfirm(null) }}
          onClose={() => setDeleteConfirm(null)}
        />
      )}

      {/* コントロール */}
      <div className="flex items-center justify-between mb-4" style={{ gap: 12, flexWrap: 'wrap' }}>
        <div className="flex items-center gap-3" style={{ flexWrap: 'wrap' }}>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 'auto' }} />
          <button
            className="btn btn-outline btn-sm"
            onClick={() => navigate('/fixed-expense-settings')}
            title="固定費の登録・修正と、対象月の支払予定生成を行います"
          >
            固定費設定・月次生成
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>+ 支出追加</button>
        </div>
      </div>

      {isClosed && <ClosedBanner month={month} closedAt={closeData?.closed_at} />}

      {/* サマリー */}
      {summary && (
        <div className="grid-4" style={{ marginBottom: 20 }}>
          <div className="metric">
            <div className="metric-label">今月合計</div>
            <div className="metric-value">¥{fmt(summary.total)}</div>
          </div>
          <div className="metric">
            <div className="metric-label">支払済</div>
            <div className="metric-value green">¥{fmt(summary.paid)}</div>
          </div>
          <div className="metric">
            <div className="metric-label">未払い</div>
            <div className={`metric-value ${summary.unpaid > 0 ? 'red' : 'green'}`}>¥{fmt(summary.unpaid)}</div>
          </div>
          <div className="metric">
            <div className="metric-label">未払い件数</div>
            <div className={`metric-value ${summary.unpaid_count > 0 ? 'red' : 'green'}`}>{summary.unpaid_count}件</div>
          </div>
        </div>
      )}

      {loading ? <div className="loading">LOADING...</div> : (
        <>
          {/* 未払い */}
          {unpaid.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">
                <span className="card-title">未払い</span>
                <span className="badge badge-red">{unpaid.length}件</span>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <SortTh label="カテゴリ" colKey="category"        sort={unpaidSort} onSort={toggleUnpaidSort} />
                      <SortTh label="内容"     colKey="description"     sort={unpaidSort} onSort={toggleUnpaidSort} />
                      <SortTh label="金額"     colKey="amount"          sort={unpaidSort} onSort={toggleUnpaidSort} />
                      <SortTh label="期日"     colKey="due_at"          sort={unpaidSort} onSort={toggleUnpaidSort} />
                      <SortTh label="支払元"   colKey="payment_account_key" sort={unpaidSort} onSort={toggleUnpaidSort} />
                      <SortTh label="支払方法" colKey="payment_method"  sort={unpaidSort} onSort={toggleUnpaidSort} />
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUnpaid.map((e) => (
                      <tr key={e.id}>
                        <td><span className="badge badge-gray">{e.category}</span></td>
                        <td style={{ fontWeight: 500, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={e.description}>
                          {e.description}
                          {e.cashflow_scope !== 'business' && (
                            <span style={{ marginLeft: 4 }}>
                              <CashflowBadge scope={e.cashflow_scope} />
                            </span>
                          )}
                        </td>
                        <td className="mono">
                          {e.amount === 0
                            ? <span style={{ color: 'var(--amber)', fontSize: 12 }}>金額未定</span>
                            : <span style={{ color: e.amount < 0 ? 'var(--blue)' : undefined }}>{fmtYen(e.amount)}</span>}
                        </td>
                        <td><DueChip days={daysUntil(e.due_at)} isPaid={e.is_paid} /></td>
                        <td><AccountBadge accountKey={e.payment_account_key ?? 'business'} /></td>
                        <td style={{ fontSize: 11, color: 'var(--ink-3)' }}>{e.payment_method}</td>
                        <td>
                          {isClosed ? (
                            <span style={{ fontSize: 11, color: 'var(--ink-3)' }} title="月締め済みのため変更不可">
                              🔒 変更不可
                            </span>
                          ) : (
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button className="btn btn-sm btn-primary"
                                onClick={() => setConfirm({ id: e.id, desc: e.description })}>
                                支払済にする
                              </button>
                              <button className="btn btn-sm btn-outline"
                                onClick={() => setEditExpense(e)} title="金額・内容を編集">
                                編集
                              </button>
                              <button className="btn btn-sm btn-ghost" style={{ color: 'var(--red)' }}
                                onClick={() => setDeleteConfirm({ id: e.id, desc: e.description, isPaid: false })}
                                title="この支出を削除">
                                削除
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {isClosed && (
                <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--blue)',
                  borderTop: '1px solid var(--paper-2)', background: 'var(--blue-light)' }}>
                  ℹ この月は締め済みのため支払状態を変更できません。
                </div>
              )}
            </div>
          )}

          {/* 支払済 */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">支払済</span>
              <span className="badge badge-green">{paid.length}件</span>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <SortTh label="カテゴリ" colKey="category"        sort={paidSort} onSort={togglePaidSort} />
                    <SortTh label="内容"     colKey="description"     sort={paidSort} onSort={togglePaidSort} />
                    <SortTh label="金額"     colKey="amount"          sort={paidSort} onSort={togglePaidSort} />
                    <SortTh label="支払日"   colKey="paid_at"         sort={paidSort} onSort={togglePaidSort} />
                    <SortTh label="支払元"   colKey="payment_account_key" sort={paidSort} onSort={togglePaidSort} />
                    <SortTh label="支払方法" colKey="payment_method"  sort={paidSort} onSort={togglePaidSort} />
                    {!isClosed && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {paid.length === 0 && (
                    <tr>
                      <td colSpan={isClosed ? 6 : 7} className="text-muted" style={{ textAlign: 'center', padding: 24 }}>
                        支払済なし
                      </td>
                    </tr>
                  )}
                  {sortedPaid.map((e) => (
                    <tr key={e.id}>
                      <td><span className="badge badge-gray">{e.category}</span></td>
                      <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={e.description}>
                        {e.description}
                      </td>
                      <td className="mono" style={{ color: e.amount < 0 ? 'var(--blue)' : undefined }}>{fmtYen(e.amount)}</td>
                      <td className="mono" style={{ fontSize: 12 }}>{e.paid_at ?? '—'}</td>
                      <td><AccountBadge accountKey={e.payment_account_key ?? 'business'} /></td>
                      <td style={{ fontSize: 11, color: 'var(--ink-3)' }}>{e.payment_method}</td>
                      {!isClosed && (
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-sm btn-outline" style={{ fontSize: 11 }}
                              onClick={() => setUnmarkConfirm({ id: e.id, desc: e.description })}
                              title="支払済を解除して未払いに戻す">
                              解除
                            </button>
                            <button className="btn btn-sm btn-ghost" style={{ color: 'var(--red)' }}
                              onClick={() => setDeleteConfirm({ id: e.id, desc: e.description, isPaid: true })}
                              title="この支出を削除">
                              削除
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showAdd && (
        <AddExpenseModal onClose={() => setShowAdd(false)}
          onSaved={(id, desc) => { setShowAdd(false); load(); if (id) setUndoState({ id, desc }) }} />
      )}
      {editExpense && (
        <EditExpenseModal
          expense={editExpense}
          onClose={() => setEditExpense(null)}
          onSaved={() => { setEditExpense(null); load(); showToast('支出を更新しました') }}
        />
      )}
    </div>
  )
}